import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { runAdminGlobalSearch } from '../utils/adminGlobalSearch';
import { hasAccessToView } from '../constants/teamAccessPermissions';

const DEBOUNCE_MS = 320;

/**
 * Top-bar admin search: products, orders, users, categories, promo codes + quick page links.
 */
const AdminGlobalSearch = ({ currentUser, navItems = [], onNavigate }) => {
  const isTeam = currentUser?.role === 'team';
  const access = currentUser?.accessPermissions;
  const can = (view) => !isTeam || hasAccessToView(view, access, true);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    products: [],
    users: [],
    orders: [],
    categories: [],
    coupons: [],
  });
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const navMatches = useMemo(() => {
    const q = debounced.toLowerCase();
    if (q.length < 2) return [];
    return navItems.filter((item) => {
      if (!item.id) return false;
      if (isTeam && !hasAccessToView(item.id, access, true)) return false;
      const label = (item.label || '').toLowerCase();
      return label.includes(q) || String(item.id).toLowerCase().includes(q);
    }).slice(0, 8);
  }, [debounced, navItems, isTeam, access]);

  const runSearch = useCallback(async () => {
    if (debounced.length < 2) {
      setData({ products: [], users: [], orders: [], categories: [], coupons: [] });
      setLoading(false);
      return;
    }
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    try {
      const next = await runAdminGlobalSearch(debounced, {
        signal: ac.signal,
        currentUser,
      });
      if (!ac.signal.aborted) setData(next);
    } catch (e) {
      if (e.name !== 'AbortError') {
        setData({ products: [], users: [], orders: [], categories: [], coupons: [] });
      }
    } finally {
      if (!ac.signal.aborted) setLoading(false);
    }
  }, [debounced, currentUser]);

  useEffect(() => {
    if (!open) return undefined;
    runSearch();
    return () => abortRef.current?.abort();
  }, [open, runSearch]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const close = () => setOpen(false);

  const onSelectNav = (id) => {
    onNavigate(id, {});
    close();
  };

  const onSelectProduct = (p) => {
    if (can('products')) {
      const hint = (p.name || p.title || p.sku || '').trim() || String(p._id || p.id || '');
      onNavigate('products', { prefillSearch: hint });
    }
    close();
  };

  const onSelectOrder = (o) => {
    const id = o._id || o.id;
    if (id && can('orders')) onNavigate('orders', { openOrderId: String(id) });
    close();
  };

  const onSelectUser = (u) => {
    const id = u._id || u.id;
    if (id && can('users')) onNavigate('users', { openUserId: String(id) });
    close();
  };

  const onSelectCategory = () => {
    if (can('categories')) onNavigate('categories', {});
    close();
  };

  const onSelectCoupon = () => {
    if (can('revenue')) onNavigate('revenue', {});
    close();
  };

  const hasRows =
    navMatches.length > 0 ||
    data.products.length > 0 ||
    data.orders.length > 0 ||
    data.users.length > 0 ||
    data.categories.length > 0 ||
    data.coupons.length > 0;

  return (
    <div className="admin-global-search-wrap" ref={wrapRef}>
      <button
        type="button"
        className="action-btn admin-search-trigger"
        title="Search admin (Ctrl+K)"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
      >
        <i className="fas fa-search" />
      </button>

      {open && (
        <div className="admin-global-search-panel" role="dialog" aria-label="Admin search">
          <div className="admin-global-search-input-row">
            <i className="fas fa-search admin-global-search-input-icon" />
            <input
              ref={inputRef}
              type="search"
              className="admin-global-search-input"
              placeholder="Search products, orders, users, categories, pages…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoComplete="off"
              autoCorrect="off"
            />
            {loading && <i className="fas fa-spinner fa-spin admin-global-search-spinner" />}
          </div>
          <div className="admin-global-search-hint">At least 2 characters · Ctrl+K anytime</div>

          <div className="admin-global-search-results">
            {debounced.length < 2 && (
              <div className="admin-global-search-empty">Type to search across the admin panel.</div>
            )}
            {debounced.length >= 2 && !loading && !hasRows && (
              <div className="admin-global-search-empty">No matches. Try another keyword.</div>
            )}

            {navMatches.length > 0 && (
              <div className="admin-search-section">
                <div className="admin-search-section-title">Pages</div>
                {navMatches.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className="admin-search-row"
                    onClick={() => onSelectNav(item.id)}
                  >
                    <span className="admin-search-row-icon admin-search-icon-nav">
                      <i className="fas fa-compass" />
                    </span>
                    <span className="admin-search-row-text">{item.label}</span>
                  </button>
                ))}
              </div>
            )}

            {can('products') && data.products.length > 0 && (
              <div className="admin-search-section">
                <div className="admin-search-section-title">Products</div>
                {data.products.map((p) => (
                  <button
                    key={p._id || p.id}
                    type="button"
                    className="admin-search-row"
                    onClick={() => onSelectProduct(p)}
                  >
                    <span className="admin-search-row-icon admin-search-icon-product">
                      <i className="fas fa-box" />
                    </span>
                    <span className="admin-search-row-text">
                      {p.name || p.title || 'Product'}
                      {p.sku ? <span className="admin-search-meta"> · {p.sku}</span> : null}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {can('orders') && data.orders.length > 0 && (
              <div className="admin-search-section">
                <div className="admin-search-section-title">Orders</div>
                {data.orders.map((o) => (
                  <button
                    key={o._id || o.id}
                    type="button"
                    className="admin-search-row"
                    onClick={() => onSelectOrder(o)}
                  >
                    <span className="admin-search-row-icon admin-search-icon-order">
                      <i className="fas fa-shopping-cart" />
                    </span>
                    <span className="admin-search-row-text">
                      #{o.orderNumber || o._id || o.id}
                      <span className="admin-search-meta">
                        {' '}
                        · {o.status || '—'} · PKR {Number(o.total || 0).toLocaleString()}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {can('users') && data.users.length > 0 && (
              <div className="admin-search-section">
                <div className="admin-search-section-title">Users</div>
                {data.users.map((u) => (
                  <button
                    key={u._id || u.id}
                    type="button"
                    className="admin-search-row"
                    onClick={() => onSelectUser(u)}
                  >
                    <span className="admin-search-row-icon admin-search-icon-user">
                      <i className="fas fa-user" />
                    </span>
                    <span className="admin-search-row-text">
                      {u.name || u.email}
                      {u.email ? <span className="admin-search-meta"> · {u.email}</span> : null}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {can('categories') && data.categories.length > 0 && (
              <div className="admin-search-section">
                <div className="admin-search-section-title">Categories</div>
                {data.categories.map((c) => (
                  <button
                    key={c._id || c.id}
                    type="button"
                    className="admin-search-row"
                    onClick={onSelectCategory}
                  >
                    <span className="admin-search-row-icon admin-search-icon-category">
                      <i className="fas fa-tags" />
                    </span>
                    <span className="admin-search-row-text">{c.name || c.slug}</span>
                  </button>
                ))}
              </div>
            )}

            {can('revenue') && data.coupons.length > 0 && (
              <div className="admin-search-section">
                <div className="admin-search-section-title">Promo codes</div>
                {data.coupons.map((c) => (
                  <button
                    key={c._id || c.id || c.code}
                    type="button"
                    className="admin-search-row"
                    onClick={onSelectCoupon}
                  >
                    <span className="admin-search-row-icon admin-search-icon-coupon">
                      <i className="fas fa-tag" />
                    </span>
                    <span className="admin-search-row-text">{c.code || c.name || 'Coupon'}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGlobalSearch;
